import React, {lazy, Suspense, useState} from 'react';

import { IPanorama } from '@ael/core-api';
import { OfflineStudentViewModes, OnlineStudentViewModes } from '../../../../config/constants';
import { setGlobalPanorama } from '../../../globalPanoramaAccessor';
import { ProjectNotAvailableErrorOverlay } from './Loader/ErrorOverlays/ProjectNotAvailableErrorOverlay';
import { TrackingNotInitializedErrorOverlay } from './Loader/ErrorOverlays/TrackingNotInitializedErrorOverlay';
import { UnknownLoadingErrorOverlay } from './Loader/ErrorOverlays/UnknownLoadingErrorOverlay';
import { AnimatedLoader } from './Loader/Loader';
import {
  useApplicationPreInitQuery,
  useOfflineStartupQuery,
  useOnlineStudentStartupQuery,
} from './startup/startupHooks';
import { StudentApp } from './StudentApp';
import { Helmet } from 'react-helmet';

const StudentAppComponent = lazy(() =>
    import('./StudentApp').then((module) => ({ default: module.StudentApp }))
);

function handleGlobalPanoramaReady(onHandled: () => void, panorama: IPanorama) {
  setGlobalPanorama(panorama);
  onHandled();
}

type OfflineAppBuilderProps = {
  viewMode: OfflineStudentViewModes;
  backendUrl: string;
  isPanomio: boolean;
};

export const OfflineAppBuilder = (props: OfflineAppBuilderProps) => {
  const [projectViewModel] = useState(window.courseConfig);
  const branding = {
    tagline: projectViewModel.settings.tagLine,
    logoUrl: window.logoUrl,
  };
  const { data } = useOfflineStartupQuery(props.viewMode, projectViewModel, props.isPanomio);
  const [isAppReady, setIsAppReady] = useState(false);

  if (!data){
    return null;
  }

  if (data.trackingResult && !data.trackingResult.isSuccess) {
    return <TrackingNotInitializedErrorOverlay />;
  }

  const isLightModeEnabled = data.projectViewModel.config.theme.isLightThemeEnabled;
  return (
    <>
      <Helmet>
        <html className={isLightModeEnabled ? 'light' : 'dark'} />
      </Helmet>
      <AnimatedLoader
        isAppReady={isAppReady}
        isPanomio={props.isPanomio}
        branding={branding}
        isLightModeEnabled={isLightModeEnabled}
      />
      {data.trackingResult.isSuccess && (
        <StudentApp
          backendUrl={props.backendUrl}
          viewMode={props.viewMode}
          hideStudentHeader={false}
          isInPreview={false}
          onStudentReady={(panorama) => handleGlobalPanoramaReady(() => setIsAppReady(true), panorama)}
          projectViewModel={projectViewModel}
          persistenceTrackingClient={data.trackingResult.trackingClient}
          persistedTrackingData={data.trackingResult.persistedTrackingData}
        />
      )}
    </>
  );
};

type AppBuilderProps = {
  viewMode: OnlineStudentViewModes;
  isInPreview: boolean;
  isPanomio: boolean;
  backendUrl: string;
  hideStudentHeader: boolean;
};

export const AppBuilder = (props: AppBuilderProps) => {
  const { data } = useApplicationPreInitQuery(props.viewMode);
  const { data: startupData } = useOnlineStudentStartupQuery(data, props.viewMode);

  const [isAppReady, setIsAppReady] = useState(false);

  if (!data) {
    return null;
  }

  if (!data.preInitData.isSuccess) {
    if (data.preInitData.reason === 'not found') {
      return <ProjectNotAvailableErrorOverlay />;
    } else {
      return <UnknownLoadingErrorOverlay />;
    }
  }

  if (startupData && startupData.trackingResult && !startupData.trackingResult.isSuccess) {
    return <TrackingNotInitializedErrorOverlay />;
  }

  const isLightModeEnabled = data.preInitData.preInitData.theme.isLightThemeEnabled;

  return (
    <>
      <Helmet>
        <html className={isLightModeEnabled ? 'light' : 'dark'} />
      </Helmet>
      <AnimatedLoader
        isAppReady={isAppReady}
        isPanomio={props.isPanomio}
        branding={data.preInitData.preInitData.branding}
        isLightModeEnabled={isLightModeEnabled}
      />
      {startupData?.trackingResult?.isSuccess === true && (
          <Suspense>
          <StudentAppComponent
            backendUrl={props.backendUrl}
            hideStudentHeader={props.hideStudentHeader}
            viewMode={props.viewMode}
            onStudentReady={(panorama) => handleGlobalPanoramaReady(() => setIsAppReady(true), panorama)}
            isInPreview={props.isInPreview}
            projectViewModel={startupData.projectViewModel}
            persistenceTrackingClient={startupData.trackingResult.trackingClient}
            persistedTrackingData={startupData.trackingResult.persistedTrackingData}
          />
          </Suspense>
      )}
    </>
  );
};
